import { toRelativeUrl } from '@okta/okta-auth-js';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { adminBarData, assetControlBarData, BarData, deskbookingNav, navItemText, reportsBarData, skillsBarData } from './BarData';
import './NavBar.css';
import { useStyles } from './NavBar.styles';
import logoutIcon from '../../images/log-out.svg';
import { useOktaAuth } from '@okta/okta-react';
import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { IUser } from '../../../interfaces/IUser';
import { useAppRoles } from '../../../helpers/useAppRoles';
import { isInternalEmail } from '../../../helpers/utils';
import { AppRole } from '../../../interfaces/IAsset';
import { useTalentAccess } from '../../../helpers/useTalentAccess';
import { useT } from '../../../i18n';


interface IProps {
  active: boolean;
  setSideMenu: () => void;
  updateIsAdmin: (value: boolean) => void;
  updateIsBlocked: (value: boolean) => void;
  updateUser: (value: IUser) => void;
}

const NavBar = (props: IProps) => {
  const { updateIsAdmin, updateIsBlocked, updateUser } = props;
  const location = useLocation();
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBlocked, setIsBlocked] = useState(false);
  const [isSpecial, setIsSpecial] = useState(false);
  // Guards against firing registerUser more than once while the created user
  // is not yet visible in the getUserByEmail refetch (eventual consistency).
  const hasAttemptedRegister = useRef(false);
  const { authState, oktaAuth } = useOktaAuth();
  const { hasAny: hasAppRole } = useAppRoles();
  const showAssetControl = hasAppRole(
    AppRole.ASSET_ADMIN,
    AppRole.ASSET_VIEWER,
    AppRole.DESKBOOKING_ADMIN,
    AppRole.SUPER_ADMIN,
  );
  const showReports = hasAppRole(
    AppRole.REPORTS_VIEWER,
    AppRole.ASSET_ADMIN,
    AppRole.ASSET_VIEWER,
    AppRole.DESKBOOKING_ADMIN,
    AppRole.SUPER_ADMIN,
  );
  // Not a plain role check: a person also gets in through their own profile,
  // and the module can be switched off server-side while it rolls out.
  const {
    canSee: showSkills,
    showPending: skillsPending,
    deskbookingEnabled: showDeskbooking,
  } = useTalentAccess();
  const canAccessAdmin =
    isAdmin ||
    hasAppRole(
      AppRole.SUPER_ADMIN,
      AppRole.DESKBOOKING_ADMIN,
      AppRole.LUNCH_ADMIN,
      AppRole.ASSET_ADMIN,
    );
  const classes = useStyles();
  const t = useT();
  const history = useHistory();
  // Active item is derived from the URL: routes outside the nav (e.g. /perfil)
  // must not highlight any tab.
  const isItemActive = (path: string): boolean =>
    path === '/'
      ? location.pathname === '/'
      : location.pathname === path || location.pathname.startsWith(`${path}/`);
  // Grouping only pays off when there is something to group against: with
  // Deskbooking alone, a menu would just add a click in front of both screens.
  // Guatemala uses the app for the Skill Matrix only: no Deskbooking entry at all.
  const groupDeskbooking =
    showDeskbooking && (showSkills || showAssetControl || showReports || canAccessAdmin);
  const deskbookingActive = deskbookingNav.paths.some(isItemActive);
  const [deskMenuAnchor, setDeskMenuAnchor] = useState<HTMLElement | null>(null);
  const DeskIcon = deskbookingNav.image;
  const changeURL = useCallback(
    (path: string, text: string) => {
      history.replace(toRelativeUrl(path, window.location.origin));
    },
    [history]
  );
  const goTo = (item: { path: string; textKey: string }) => {
    setDeskMenuAnchor(null);
    changeURL(item.path, navItemText(item));
    props.setSideMenu();
  };
  // The items are <li>s, so Enter and Space have to be wired by hand for
  // anyone moving through the bar with the keyboard.
  const onItemKeyDown = (item: { path: string; textKey: string }) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goTo(item);
    }
  };

  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then(
        (result) => {
          if (result.email) {
            setUserEmail(result.email);
          }
          if (result.given_name && result.family_name) {
            setUserName(result.given_name + ' ' + result.family_name);
          }
        },
        () => undefined
      );
    }
  }, [authState, oktaAuth]);

  const userQuery = gql`
    query ($email: String!) {
      users: getUserByEmail(email: $email) {
        email
        name
        role
        areaId
        phone
        blocked
        language
      }
    }
  `;

  const [getUserData, userData] = useLazyQuery(userQuery, {
    variables: { email: userEmail },
    fetchPolicy: 'network-only',
  });

  const registerUserMutation = gql`
    mutation ($name: String) {
      result: registerUser(name: $name) {
        email
        name
      }
    }
  `;

  const [registerUser] = useMutation(registerUserMutation);

  useEffect(() => {
    if (userEmail) {
      getUserData();
    }
  }, [userEmail, getUserData]);

  useEffect(() => {
    if (userData.data) {
      if (userData.data.users.length > 0) {
        updateUser(userData.data.users[0]);
        if (userData.data.users[0].role === 'Admin') {
          setIsAdmin(true);
        }
        if (userData.data.users[0].areaId !== 1) {
          setIsSpecial(true);
        }
        if (userData.data.users[0].blocked === 1) {
          setIsBlocked(true);
          updateIsBlocked(true);
        }
      } else if (
        userEmail &&
        isInternalEmail(userEmail) &&
        !hasAttemptedRegister.current
      ) {
        hasAttemptedRegister.current = true;
        registerUser({ variables: { name: userName || undefined } })
          .then(() => {
            getUserData();
          })
          .catch(() => {
            // Allow another attempt on a later render if registration failed
            // (e.g. transient network error) instead of locking the user out.
            hasAttemptedRegister.current = false;
          });
      }
    }
  }, [userData.data, userEmail, userName, registerUser, getUserData, updateIsAdmin, updateIsBlocked, updateUser]);

  useEffect(() => {
    updateIsAdmin(canAccessAdmin);
  }, [canAccessAdmin, updateIsAdmin]);

  return (
    <nav
      id="nav-items"
      className={`${classes.NavMenu} ${props.active ? 'open' : ''}`}
    >
      <ul className={classes.navItemContainer}>
        {/*
          Deskbooking first. On desktop it is a single entry that opens a menu,
          so the bar lists apps rather than screens; in the stacked mobile menu
          there is room to show both screens under the app name, which saves a
          tap on a phone. Someone with Deskbooking only gets both screens flat.
        */}
        {groupDeskbooking && (
        <>
        <li className={classes.deskDesktop} style={{ '--app-color': deskbookingNav.color } as React.CSSProperties}>
          <button
            type="button"
            id="nav-deskbooking"
            className={`${classes.navButton} ${classes.menuTrigger} ${deskbookingActive ? 'active' : ''}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(deskMenuAnchor)}
            aria-controls={deskMenuAnchor ? 'nav-deskbooking-menu' : undefined}
            onClick={(e) => setDeskMenuAnchor(e.currentTarget)}
          >
            <DeskIcon className={classes.buttonIcon} />
            <span className={`${classes.buttonText} ${deskbookingActive ? 'active' : ''}`}>
              {navItemText(deskbookingNav)}
            </span>
            <svg
              className={`${classes.chevron} ${deskMenuAnchor ? 'open' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <Menu
            id="nav-deskbooking-menu"
            anchorEl={deskMenuAnchor}
            open={Boolean(deskMenuAnchor)}
            onClose={() => setDeskMenuAnchor(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            classes={{ paper: classes.menuPaper, list: classes.menuList }}
            MenuListProps={{ 'aria-labelledby': 'nav-deskbooking' }}
          >
            {BarData.map((item) => {
              const Icon = item.image;
              const active = isItemActive(item.path);
              return (
                <MenuItem
                  key={item.path}
                  selected={active}
                  aria-current={active ? 'page' : undefined}
                  className={classes.menuItem}
                  onClick={() => goTo(item)}
                >
                  <span className={classes.menuIconTile}>
                    <Icon className={classes.buttonIcon} />
                  </span>
                  <span className={classes.menuText}>
                    <span className={classes.menuTitle}>{navItemText(item)}</span>
                    <span className={classes.menuHint}>{t(item.hintKey)}</span>
                  </span>
                </MenuItem>
              );
            })}
          </Menu>
        </li>
        <li className={classes.deskMobile} style={{ '--app-color': deskbookingNav.color } as React.CSSProperties}>
          <p className={classes.groupLabel} id="nav-deskbooking-mobile">
            {navItemText(deskbookingNav)}
          </p>
          <ul className={classes.subList} aria-labelledby="nav-deskbooking-mobile">
            {BarData.map((item) => {
              const Icon = item.image;
              const active = isItemActive(item.path);
              return (
                <li
                  key={item.path}
                  role="link"
                  tabIndex={0}
                  aria-current={active ? 'page' : undefined}
                  className={`${classes.navButton} ${active ? 'active' : ''}`}
                  onClick={() => goTo(item)}
                  onKeyDown={onItemKeyDown(item)}
                >
                  <Icon className={classes.buttonIcon}></Icon>
                  <p className={`${classes.buttonText} ${active ? 'active' : ''}`}>
                    {navItemText(item)}
                  </p>
                </li>
              );
            })}
          </ul>
        </li>
        </>
        )}
        {(() => {
          // Then the other tools, and Admin always last.
          let items: typeof skillsBarData =
            groupDeskbooking || !showDeskbooking ? [] : BarData.slice();
          if (showSkills) items = items.concat(skillsBarData);
          if (showAssetControl) items = items.concat(assetControlBarData);
          if (showReports) items = items.concat(reportsBarData);
          if (canAccessAdmin) items = items.concat(adminBarData);
          return items;
        })().map((item, i, all) => {
          const Icon = item.image;
          const active = isItemActive(item.path);
          // When grouped, the Deskbooking menu sits before the first item.
          const startsGroup =
            i === 0 ? groupDeskbooking : item.group !== all[i - 1].group;
          return (
            <li
              key={item.path}
              role="link"
              tabIndex={0}
              aria-current={active ? 'page' : undefined}
              className={`${classes.navButton} ${startsGroup ? classes.groupStart : ''} ${
                active ? 'active' : ''
              }`}
              style={{ '--app-color': item.color } as React.CSSProperties}
              onClick={() => goTo(item)}
              onKeyDown={onItemKeyDown(item)}
            >
              <Icon className={`${classes.buttonIcon} ${active ? 'active' : ''}`}></Icon>
              <p className={`${classes.buttonText} ${active ? 'active' : ''}`}>
                {navItemText(item)}
              </p>
              {item.group === 'skills' && skillsPending && (
                <span
                  className={classes.pendingDot}
                  role="img"
                  aria-label={t('nav.skillsPending')}
                  title={t('nav.skillsPending')}
                />
              )}
            </li>
          );
        })}
        <li
          className={`${classes.navButton} ${classes.logoutItem}`}
          onClick={() => oktaAuth.signOut()}
        >
          <img src={logoutIcon} className={classes.buttonIcon} alt="" aria-hidden="true" />
          <p className={classes.buttonText}>{t('nav.logout')}</p>
        </li>
      </ul>
    </nav>
  );
};

export default NavBar;